import {
  type ClientMessage,
  decodeServerMessage,
  encodeClientMessage,
  PROTOCOL_VERSION,
  type ServerMessage,
  WS_PATH,
} from '@md/shared';

/**
 * Client WebSocket (spec §5.1).
 *
 * Frontière de confiance : **tout** message sortant est validé avant émission
 * et **tout** message entrant est validé avant d'être remis à l'application.
 * Un serveur d'une autre version produit donc une erreur explicite, jamais un
 * `undefined` silencieux au milieu de la boucle de jeu.
 */
export interface NetClientHandlers {
  onOpen(): void;
  onMessage(message: ServerMessage): void;
  onClose(): void;
  /** Message invalide ou erreur de transport. */
  onInvalid(reason: string): void;
}

export interface NetClient {
  connect(): void;
  send(message: ClientMessage): void;
  isOpen(): boolean;
  /** Une socket est ouverte en ce moment même (poignée de main en cours). */
  isConnecting(): boolean;
  close(): void;
}

/** URL du WebSocket déduite de l'origine courante (HTTP → ws, HTTPS → wss). */
export function defaultWsUrl(location: Location = window.location): string {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}${WS_PATH}`;
}

export interface NetClientOptions {
  readonly url?: string;
  readonly handlers: NetClientHandlers;
  /** Fabrique de socket — injectable pour les tests. */
  readonly factory?: (url: string) => WebSocket;
}

export function createNetClient(options: NetClientOptions): NetClient {
  const url = options.url ?? defaultWsUrl();
  const factory = options.factory ?? ((target: string) => new WebSocket(target));
  const { handlers } = options;
  let socket: WebSocket | null = null;
  let closedByUs = false;
  /**
   * File d'attente des messages émis avant l'ouverture de la socket : au
   * chargement de la page, la reprise de session (`rejoinRoom`) part AVANT que
   * la connexion soit établie. Sans cette file, le message serait perdu.
   */
  let outbox: string[] = [];

  const flush = (): void => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }
    for (const payload of outbox) {
      socket.send(payload);
    }
    outbox = [];
  };

  return {
    connect(): void {
      if (socket) {
        return;
      }
      closedByUs = false;
      // Chaque écouteur ne parle QUE de la socket qui l'a installé : après une
      // reconnexion, l'événement `close` tardif de l'ancienne socket ne doit
      // pas effacer la nouvelle (elle deviendrait invisible et une troisième
      // socket s'ouvrirait derrière).
      const current = factory(url);
      socket = current;
      current.addEventListener('open', () => {
        if (socket !== current) {
          return;
        }
        // Poignée de main : le serveur refuse une version incompatible.
        current.send(encodeClientMessage({ type: 'hello', protocolVersion: PROTOCOL_VERSION }));
        flush();
        handlers.onOpen();
      });
      current.addEventListener('message', (event: MessageEvent) => {
        if (socket !== current) {
          return;
        }
        const decoded = decodeServerMessage(event.data);
        if (!decoded.ok) {
          handlers.onInvalid(decoded.error);
          return;
        }
        handlers.onMessage(decoded.value);
      });
      current.addEventListener('close', () => {
        if (socket !== current) {
          return;
        }
        socket = null;
        handlers.onClose();
      });
      current.addEventListener('error', () => {
        if (socket !== current || closedByUs) {
          return;
        }
        handlers.onInvalid('connexion WebSocket impossible');
      });
    },

    send(message: ClientMessage): void {
      outbox.push(encodeClientMessage(message));
      flush();
    },

    isOpen(): boolean {
      return socket !== null && socket.readyState === WebSocket.OPEN;
    },

    isConnecting(): boolean {
      return socket !== null && socket.readyState === WebSocket.CONNECTING;
    },

    close(): void {
      closedByUs = true;
      outbox = [];
      socket?.close();
      socket = null;
    },
  };
}
